import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { attemptsApi, testsApi, percentileApi, duelsApi, wrongRetestApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Circle, Share2, Trophy, Target, Timer, Zap, Volume2, VolumeX, Users, RefreshCw, Swords } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";
import { celebrate } from "@/pages/student/LiveExam";
import { toast } from "sonner";

export default function Result() {
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [test, setTest] = useState(null);
  const [tpq, setTpq] = useState({});
  const [rank, setRank] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const utterRef = useRef(null);

  useEffect(() => {
    attemptsApi.get(attemptId).then(async (a) => {
      setAttempt(a);
      const t = await testsApi.get(a.test_id, true);
      setTest(t);
      try { const raw = sessionStorage.getItem(`tpq_${attemptId}`); if (raw) setTpq(JSON.parse(raw)); } catch {}
      try { const r = await percentileApi.get(a.test_id, a.score); setRank(r); } catch {}
    });
    return () => { try { window.speechSynthesis?.cancel(); } catch {} };
  }, [attemptId]);

  const playExplanation = (q, id) => {
    if (!("speechSynthesis" in window)) { toast.error("Voice not supported on this browser"); return; }
    window.speechSynthesis.cancel();
    if (playingId === id) { setPlayingId(null); return; }
    const text = q?.explanation || "No explanation provided for this question.";
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1.0; utter.pitch = 1.0; utter.lang = "en-IN";
    utter.onstart = () => setPlayingId(id);
    utter.onend = () => setPlayingId(null);
    utter.onerror = () => { setPlayingId(null); toast.error("Voice unavailable on this device"); };
    utterRef.current = utter;
    // set immediately so UI reacts even if onstart is delayed
    setPlayingId(id);
    window.speechSynthesis.speak(utter);
  };

  const percent = useMemo(() => attempt?.total_marks ? Math.round((attempt.score / attempt.total_marks) * 100) : 0, [attempt]);

  useEffect(() => {
    if (attempt && percent >= 50) celebrate();
  }, [attempt, percent]);

  if (!attempt || !test) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const qmap = Object.fromEntries((test.questions || []).map(q => [q.id, q]));
  const chartData = Object.entries(attempt.subject_stats || {}).map(([s, st]) => ({
    subject: s, correct: st.correct, wrong: st.wrong,
  }));
  const attempted = (attempt.correct || 0) + (attempt.wrong || 0);
  const accuracy = attempted ? Math.round((attempt.correct / attempted) * 100) : 0;
  const totalTime = Object.values(tpq).reduce((s, x) => s + x, 0);
  const avgSec = attempted ? Math.round(totalTime / attempted) : 0;
  // simple percentile guess: bell around 60% mean
  const percentile = Math.max(1, Math.min(99, Math.round(50 + (percent - 60) * 1.2)));
  const grade = percent >= 85 ? "Elite" : percent >= 70 ? "Strong" : percent >= 50 ? "Good" : percent >= 30 ? "Room to grow" : "Restart needed";
  const gradeColor = percent >= 70 ? "text-emerald-600" : percent >= 50 ? "text-primary" : percent >= 30 ? "text-amber-600" : "text-destructive";

  const share = () => {
    const text = `I scored ${attempt.score}/${attempt.total_marks} (${percent}%) on ${test.title} @ Mock Test Club — predicted percentile ${percentile}. Try it: `;
    const url = window.location.origin;
    const wa = `https://wa.me/?text=${encodeURIComponent(text + url)}`;
    if (navigator.share) {
      navigator.share({ title: "Mock Test Club score", text, url }).catch(() => window.open(wa, "_blank"));
    } else { window.open(wa, "_blank"); }
    toast.success("Sharing…");
  };

  const challengeFriend = async () => {
    try {
      const d = await duelsApi.create({ test_id: test.id, attempt_id: attempt.id });
      const link = `${window.location.origin}/duel/${d.code}`;
      const text = `⚔️ I scored ${attempt.score}/${attempt.total_marks} on ${test.title}. Think you can beat me? Mock Test Club duel:`;
      const wa = `https://wa.me/?text=${encodeURIComponent(text + " " + link)}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      window.open(wa, "_blank");
      toast.success(`Duel link copied · code ${d.code}`);
    } catch { toast.error("Could not create duel"); }
  };

  const retestWrong = async () => {
    try {
      const t = await wrongRetestApi.create(attempt.id);
      toast.success("Wrong-only drill ready — go!");
      window.location.href = `/student/exam/${t.id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build drill"); }
  };

  return (
    <div data-testid="result-page" className="space-y-8">
      {/* HERO CARD */}
      <Card className="en-card p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl" aria-hidden />
        <div className="absolute -right-24 top-32 h-64 w-64 rounded-full bg-accent/10 blur-3xl" aria-hidden />
        <div className="relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5" /> Result · {grade}
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-2">{test.title}</h1>
            <div className="mt-6 flex items-end gap-3">
              <div className={`font-display font-black text-6xl sm:text-7xl tabular-nums ${gradeColor}`}>{attempt.score}</div>
              <div className="text-2xl text-muted-foreground pb-2">/ {attempt.total_marks}</div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">{percent}% overall · predicted percentile <b className="text-foreground">{percentile}</b></div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to={`/student/exam/${test.id}`}><Button data-testid="retake-btn" className="rounded-full">Retake test</Button></Link>
              {(attempt.detailed || []).some(d => d.result === "wrong") && (
                <Button data-testid="retest-wrong-btn" variant="outline" className="rounded-full" onClick={retestWrong}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Retest wrong only ({attempt.wrong})
                </Button>
              )}
              <Button data-testid="challenge-btn" variant="outline" className="rounded-full" onClick={challengeFriend}><Swords className="h-4 w-4 mr-2" /> Challenge friend</Button>
              <Button data-testid="share-result" variant="ghost" className="rounded-full" onClick={share}><Share2 className="h-4 w-4 mr-2" /> Share</Button>
              <Link to="/student/tests"><Button variant="ghost" className="rounded-full">Back to tests</Button></Link>
            </div>
          </div>
          {/* score ring */}
          <div className="grid place-items-center">
            <ScoreRing pct={percent} />
          </div>
        </div>
      </Card>

      {/* RANK RACE */}
      {rank && rank.total > 0 && (
        <Card data-testid="rank-race" className="en-card p-6">
          <div className="flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-secondary" /><h3 className="font-display font-semibold text-lg">Live rank race</h3></div>
          <p className="text-sm">You beat <b className="text-primary text-xl font-display">{rank.percentile}%</b> of aspirants who attempted this test ({rank.beat} of {rank.total} peers).</p>
          <div className="mt-3 h-3 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-1000" style={{ width: `${rank.percentile}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
            <span>Bottom</span><span>Top</span>
          </div>
        </Card>
      )}

      {/* STAT STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat icon={CheckCircle2} label="Correct" value={attempt.correct} tint="text-emerald-600" bg="bg-emerald-500/10" />
        <MiniStat icon={XCircle} label="Wrong" value={attempt.wrong} tint="text-destructive" bg="bg-destructive/10" />
        <MiniStat icon={Target} label="Accuracy" value={`${accuracy}%`} tint="text-primary" bg="bg-primary/10" />
        <MiniStat icon={Timer} label="Avg / question" value={`${avgSec}s`} tint="text-accent" bg="bg-accent/15" />
      </div>

      {/* SUBJECT + SPEED VS ACCURACY */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="en-card p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-lg mb-4">Subject breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="subject" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                <Bar dataKey="correct" stackId="a" fill="hsl(142 71% 45%)" radius={[8, 8, 0, 0]} />
                <Bar dataKey="wrong" stackId="a" fill="hsl(var(--destructive))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="en-card p-6">
          <div className="flex items-center gap-2 mb-4"><Zap className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold text-lg">Speed vs accuracy</h3></div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You averaged <b className="text-foreground">{avgSec}s</b> per attempted question at <b className="text-foreground">{accuracy}%</b> accuracy.
          </p>
          <div className="mt-4 p-3 rounded-xl bg-muted/50 text-sm">
            {accuracy >= 75 && avgSec <= 90 ? "🚀 Sharp and fast — keep this rhythm on exam day." :
             accuracy >= 75 ? "🎯 Accurate but slow — practice sectional timing next." :
             avgSec <= 75 ? "⚡ Fast but leaking marks — slow down 10 s on tough questions." :
             "🧭 Focus on accuracy first, speed follows naturally."}
          </div>
        </Card>
      </div>

      {/* QUESTION REVIEW */}
      <Card className="en-card p-6">
        <h3 className="font-display font-semibold text-lg mb-4">Question review</h3>
        <div className="space-y-3">
          {(attempt.detailed || []).map((d, i) => {
            const q = qmap[d.question_id];
            const Icon = d.result === "correct" ? CheckCircle2 : d.result === "wrong" ? XCircle : Circle;
            const tint = d.result === "correct" ? "text-emerald-600" : d.result === "wrong" ? "text-destructive" : "text-muted-foreground";
            const spent = tpq[d.question_id];
            const isPlaying = playingId === d.question_id;
            const showVoice = d.result === "wrong" && q?.explanation;
            return (
              <div key={i} className="p-4 rounded-xl border border-border" data-testid={`review-q-${i}`}>
                <div className="flex items-start gap-3">
                  <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${tint}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge variant="secondary" className="rounded-full">Q{i+1}</Badge>
                      {q && <Badge variant="outline" className="rounded-full">{q.subject}</Badge>}
                      <Badge variant="outline" className={`rounded-full ${tint}`}>{d.result}</Badge>
                      <Badge variant="outline" className="rounded-full">{d.marks_awarded > 0 ? "+" : ""}{d.marks_awarded}</Badge>
                      {spent != null && <Badge variant="outline" className="rounded-full text-xs"><Timer className="h-3 w-3 mr-1" />{spent}s</Badge>}
                      {d.confidence && <Badge variant="outline" className={`rounded-full text-xs ${d.confidence === "sure" ? "text-emerald-600" : "text-amber-600"}`}>{d.confidence === "sure" ? "🎯 Sure" : "🎲 Guess"}</Badge>}
                      {showVoice && (
                        <button data-testid={`play-voice-${i}`} onClick={() => playExplanation(q, d.question_id)}
                          className={`rounded-full px-2.5 py-1 text-xs font-medium flex items-center gap-1 ${isPlaying ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary hover:bg-primary/20"}`}>
                          {isPlaying ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                          {isPlaying ? "Stop" : "Hear it (30s)"}
                        </button>
                      )}
                    </div>
                    {q && <div className="text-sm">{q.text}</div>}
                    <div className="text-xs mt-2 text-muted-foreground">
                      Your answer: <b>{(d.user_answer || []).join(", ") || "—"}</b> · Correct: <b>{(d.correct || []).join(", ")}</b>
                    </div>
                    {q?.explanation && <div className="text-xs mt-2 p-2 rounded-lg bg-muted/60">💡 {q.explanation}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tint, bg }) {
  return (
    <Card className="en-card p-5">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl grid place-items-center ${bg} ${tint}`}><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-widest">{label}</div>
          <div className={`font-display font-bold text-2xl ${tint}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function ScoreRing({ pct }) {
  const size = 180;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} stroke="hsl(var(--muted))" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} stroke="hsl(var(--primary))" fill="none"
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-display font-bold text-4xl">{pct}%</div>
          <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Score</div>
        </div>
      </div>
    </div>
  );
}
